import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { ReactComponent as AiGenerateIcon } from '@elementary/common-ui/assets/16x16/ai-generate.svg';
import { ReactComponent as UndoIcon } from '@elementary/common-ui/assets/16x16/undo.svg';
import { ReactComponent as ApproveIcon } from '@elementary/common-ui/assets/v.svg';
import {
  TextField,
  Flex,
  IconButton,
  FlexColumn,
  FeedbackButtons,
} from '@elementary/common-ui/components';
import { events } from '@elementary/common-ui/hooks/tracking/analytics';
import { Skeleton, Tooltip } from '@mui/material';
import CopyButton from 'shared/components/CopyButton';
import { radius, space } from '@elementary/common-ui/style/utils';

export const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    padding: 0 !important;
    min-height: 100% !important;
  }
`;

const Wrapper = styled(Flex)`
  margin: ${space('lg', 0)};
  gap: ${space('lg')};
  height: 74px;
  max-height: 74px;
  overflow: visible;
`;

const dashedBorderStyle = css`
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23B6BABE' stroke-width='2' stroke-dasharray='5%2C8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
`;

const ContentWrapper = styled(Flex)<{
  $isEditing: boolean;
  $isEdited: boolean;
}>`
  width: 100%;
  gap: ${space('sm')};
  background: ${({ $isEditing, $isEdited, theme }) =>
    $isEditing || $isEdited
      ? theme.palette.surface.soft
      : theme.palette.surface.base};

  ${({ $isEdited }) => $isEdited && dashedBorderStyle};

  outline: ${({ theme, $isEditing, $isEdited }) =>
    !$isEdited &&
    `${$isEditing ? '2px' : '1px'} solid ${theme.palette.border.default}`};

  z-index: ${({ $isEditing, theme }) =>
    $isEditing ? theme.zIndex.fab : 'auto'};
  border-radius: ${radius('lg')};
  padding: ${space('md', 'mdMinus', 'md', 'lg')};
  height: ${({ $isEditing }) => ($isEditing ? 'fit-content' : '100%')};
  min-height: 100%;
  max-height: ${({ $isEditing }) => ($isEditing ? '180px' : '100%')};
  overflow-y: scroll;
  transition: max-height 0.1s ease-in-out;
`;

const StyledIconButton = styled(IconButton)`
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
`;

const StyledCopyButton = styled(CopyButton)`
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
`;

const StickyButtonsWrapper = styled(FlexColumn)`
  justify-content: space-between;
  align-items: flex-end;
  position: sticky;
  top: 0;
`;

const ApproveIconStyled = styled(ApproveIcon)`
  path {
    stroke: ${({ theme }) => theme.palette.icon.primary};
    stroke-width: 1;
  }
`;

const LoadingState = () => (
  <FlexColumn sx={{ width: '100%', gap: 1, justifyContent: 'center' }}>
    {[85, 90, 75].map((i) => (
      <Skeleton
        key={i}
        sx={{ borderRadius: radius('md') }}
        variant="rectangular"
        width={`${i}%`}
        height="12px"
      />
    ))}
  </FlexColumn>
);

type StringValueEditItemProps = {
  value?: string;
  initialValue?: string;
  onChange: (val: string) => void;
  readOnly?: boolean;
  onGenerate?: () => void;
  isLoading?: boolean;
  isGenerateEnabled?: boolean;
  isValueGenerated?: boolean;
};

const StringValueEditItem = ({
  value = '',
  initialValue = '',
  onChange,
  readOnly,
  onGenerate,
  isLoading,
  isGenerateEnabled,
  isValueGenerated,
}: StringValueEditItemProps) => {
  const [textFieldValue, setTextFieldValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [prevValues, setPrevValues] = useState<string[]>([]);

  const handleApply = (val: string) => {
    setIsEditing(false);
    if (val !== value) {
      onChange(val);
      if (value !== (prevValues[prevValues.length - 1] || ''))
        setPrevValues((prev) => [...prev, value]);
    }
  };

  const isValueChanged = textFieldValue !== initialValue;
  const isEdited = !isEditing && isValueChanged && !isLoading;
  const isUndoEnabled = !isLoading && (!!prevValues.length || isValueChanged);

  const handleUndoClick = () => {
    const valueToSet = prevValues.length
      ? prevValues[prevValues.length - 1]
      : initialValue;
    setTextFieldValue(valueToSet);
    onChange(valueToSet);
    setPrevValues((prev) => prev.slice(0, -1));
  };

  useEffect(() => {
    setTextFieldValue(value);
  }, [value]);

  return (
    <Wrapper>
      <ContentWrapper $isEditing={isEditing} $isEdited={isEdited}>
        {isLoading && <LoadingState />}

        {!isLoading && (
          <StyledTextField
            fullWidth
            value={textFieldValue}
            multiline
            variant="standard"
            InputProps={{ disableUnderline: true }}
            readOnly={readOnly}
            placeholder="Add description..."
            onChange={(e) => setTextFieldValue(e.target.value)}
            onFocus={() => setIsEditing(true)}
            onBlur={() => handleApply(textFieldValue)}
          />
        )}

        {!readOnly && (
          <StickyButtonsWrapper>
            <Flex>
              <StyledCopyButton
                sx={{ visibility: textFieldValue ? 'visible' : 'hidden' }}
                iconSize={12}
                textToCopy={textFieldValue}
                disabled={isLoading}
              />

              <Tooltip
                title={
                  isGenerateEnabled
                    ? 'Generate with AI'
                    : 'Access to AI features and agents required'
                }
              >
                <div>
                  <StyledIconButton
                    onClick={onGenerate}
                    disabled={isLoading || !isGenerateEnabled}
                  >
                    <AiGenerateIcon />
                  </StyledIconButton>
                </div>
              </Tooltip>
            </Flex>

            {!!isEditing && (
              <Tooltip title="Apply">
                <StyledIconButton onClick={() => handleApply(textFieldValue)}>
                  <ApproveIconStyled />
                </StyledIconButton>
              </Tooltip>
            )}

            {!isEditing && isValueGenerated && (
              <FeedbackButtons
                message={textFieldValue}
                eventType={events.AI_GENERATED_DESCRIPTION_FEEDBACK}
              />
            )}
          </StickyButtonsWrapper>
        )}
      </ContentWrapper>

      {!readOnly && (
        <FlexColumn sx={{ justifyContent: 'center' }}>
          <Tooltip title="Undo">
            <StyledIconButton
              variant="outlined"
              disabled={!isUndoEnabled}
              onClick={handleUndoClick}
            >
              <UndoIcon width={16} />
            </StyledIconButton>
          </Tooltip>
        </FlexColumn>
      )}
    </Wrapper>
  );
};

export default StringValueEditItem;
